import React, { useEffect, useContext, useState, useRef, useCallback, useMemo } from "react";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  formatPrice,
  getSystemSettings,
  customStyle,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "react-moment";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

//pages & includes
import ReportSidebar from "./ReportSidebar";

//context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import { UserContext } from "../../../../contexts/User";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import { ToastError } from "../../../../functions/ToastShow";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import ReportExcelPrintGap from "../../../components/Reports/ReportExcelPrintGap";

const SupplierPurchaseGroupedTable = (props) => {
  const {
    header,
    generatedReport,
    heading,
    reportData,
    startDate,
    endDate,
    branch,
    printRef,
    type = "print",
    generalSettings,
  } = props;

  const tableHeading = () => (
    <>
      <h3 className="text-center xslg-text my-2">{getSystemSettings(generalSettings, "siteName")}</h3>
      <h3 className="text-center xslg-text my-2">{heading}</h3>
      {branch?.show && branch?.name && <p className="text-center my-2">{branch.name}</p>}
      <p className="text-center my-2">
        {startDate && endDate ? (
          <>
            FROM&nbsp;
            <Moment format="LL">{startDate}</Moment>
            &nbsp;TO&nbsp;
            <Moment format="LL">{endDate}</Moment>
          </>
        ) : (
          <>
            Date&nbsp;
            <Moment format="LL">{startDate}</Moment>
          </>
        )}
      </p>
    </>
  );

  const TotalRow = ({ label, totals, activeCol = 0, isGrand = false }) => (
    <tr style={{ backgroundColor: isGrand ? "#e9ecef" : "#f8f9fa", fontWeight: "bold" }}>
      {[0, 1, 2].map((colIdx) => (
        <th key={colIdx} className="border-1 border sm-text" style={{ fontWeight: "bold" }}>
          {colIdx === activeCol ? label : ""}
        </th>
      ))}
      {header.slice(3).map((col, i) => (
        <th key={i} className="border-1 border sm-text text-right" style={{ fontWeight: "bold" }}>
          {col.total ? formatPrice(totals[col.key] || 0) : ""}
        </th>
      ))}
    </tr>
  );

  const renderRows = useCallback(
    (data, level = 0, currentNames = { branch: "", supplier: "", item: "" }) => {
      if (!data) return { rows: [], totals: {} };

      let currentLevelTotals = {};
      header.forEach((col) => {
        if (col.total) currentLevelTotals[col.key] = 0;
      });

      if (Array.isArray(data)) {
        let rows = [];
        data.forEach((item, index) => {
          header.forEach((col) => {
            if (col.total) {
              const val = typeof col?.render === "function" ? col.render(item) : item[col?.key];
              currentLevelTotals[col.key] += parseFloat(val) || 0;
            }
          });

          rows.push(
            <tr key={index}>
              <td className="border-1 border sm-text"></td>
              <td className="border-1 border sm-text"></td>
              <td className="border-1 border sm-text"></td>
              {header.slice(3).map((col, idx) => (
                <td key={idx} className="text-capitalize align-middle border-1 border sm-text">
                  {typeof col?.render === "function" ? col.render(item, index) : item[col?.key]}
                </td>
              ))}
            </tr>
          );
        });

        return { rows, totals: currentLevelTotals };
      }

      let allRows = [];
      const keys = Object.keys(data);

      keys.forEach((key, index) => {
        const names = { ...currentNames };
        if (level === 0) names.branch = key;
        if (level === 1) names.supplier = key;
        if (level === 2) names.item = key;

        const headerRow = (
          <tr key={`${level}-${key}-header`}>
            <th className="border-1 border sm-text" style={{ minWidth: "150px", fontWeight: "bold", backgroundColor: "#f1f1f1" }}>
              {level === 0 ? key : ""}
            </th>
            <th className="border-1 border sm-text" style={{ fontWeight: "bold", backgroundColor: "#f1f1f1" }}>
              {level === 1 ? key : ""}
            </th>
            <th className="border-1 border sm-text" style={{ fontWeight: "bold", backgroundColor: "#f1f1f1" }}>
              {level === 2 ? key : ""}
            </th>
            {header.slice(3).map((_, i) => (
              <th key={i} className="border-1 border" style={{ backgroundColor: "#f1f1f1" }}></th>
            ))}
          </tr>
        );

        const { rows: subRows, totals: subTotals } = renderRows(data[key], level + 1, names);

        allRows.push(headerRow);
        allRows.push(...subRows);

        let label = "";
        if (level === 2) label = `Item Total (${key})`;
        else if (level === 1) label = `Supplier Total (${key})`;
        else if (level === 0) label = `Branch Total (${key})`;

        allRows.push(<TotalRow key={`${level}-${key}-total`} label={label} totals={subTotals} activeCol={level} />);

        if (level === 0) {
          allRows.push(
            <tr key={`${key}-gap`} style={{ height: "20px" }}>
              <td colSpan={header.length} className="border-0"></td>
            </tr>
          );
        }

        Object.keys(subTotals).forEach((tKey) => {
          currentLevelTotals[tKey] += subTotals[tKey];
        });
      });

      return { rows: allRows, totals: currentLevelTotals };
    },
    [header]
  );

  const { rows, totals: grandTotals } = useMemo(() => renderRows(reportData), [reportData, renderRows]);

  return (
    <div className="d-none">
      <div ref={printRef}>
        {reportData && generatedReport ? (
          <div className="fk-print container-fluid mt-3">
            {type !== "excel" && tableHeading()}
            <table id="table-to-xls" className="table table-bordered table-hover mt-2">
              {type === "excel" && (
                <>
                  <tr>
                    <th colSpan={header.length}>{tableHeading()}</th>
                  </tr>
                  <tr></tr>
                </>
              )}
              <thead>
                <tr>
                  {header.map((col, i) => (
                    <th key={i} scope="col" className="border-1 border" style={{ fontSize: "0.77rem" }}>
                      {col.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="align-middle">
                {rows}
                {!branch?.removeTotal && (
                  <TotalRow label="Grand Total" totals={grandTotals} activeCol={0} isGrand={true} />
                )}
              </tbody>
            </table>
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

const SupplierPurchaseReport = () => {

  const { t } = useTranslation();
  //getting context values here

  let { loading, setLoading, dataPaginating, setDataPaginating, generalSettings } = useContext(SettingsContext);
  const { authUserInfo,getSupplier, supplierForSearch } = useContext(UserContext);
  const { getIngredientItem,ingredientItemForSearch} = useContext(RestaurantContext);

  let { branchForSearch } = useContext(RestaurantContext);
  // FoodContext not needed here after switching to supplier

  const [supplierPurchaseFilters, setSupplierPurchaseFilters] = useState({
    branch: null,
    suppliers: null,
    group_by: { label: _t(t("Supplier")), value: "Supplier" },
  });
  
  const [reportData, setReportData] = useState(null);

  const componentRef = useRef();
  const componentRefThermal = useRef();
  //settle order
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(false);
  const [option, setOption] = useState("excel");
  //useEffect == componentDidMount()

  useEffect(() => {

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [generalSettings]);

  useEffect(() => {
    if(!supplierForSearch){
      getSupplier();
    }

    if(!ingredientItemForSearch){
      getIngredientItem()
    }
    
  }, []);

  const handleOption = (e) => setOption(e.target.value);

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `@page { margin: 15px 7px; }`,
  });

  const handleBranch = (branch) => {
    setSupplierPurchaseFilters({
      ...supplierPurchaseFilters,
      branch,
    });
  };

  const handleSupplier = (suppliers) => {
    setSupplierPurchaseFilters({
      ...supplierPurchaseFilters,
      suppliers,
    });
  };

  const handleIngredientItem = (ingredientItems) => {
    setSupplierPurchaseFilters({
      ...supplierPurchaseFilters,
      ingredientItems,
    });
  };

  const handleGroupBy = (group_by) => {
    setSupplierPurchaseFilters({
      ...supplierPurchaseFilters,
      group_by,
    });
  };

  //get BranchWise reports filter
  const generateReport = () => {

    setReportData(null);
    setGeneratedReport(false);

    if (startDate === null || endDate === null) {
      ToastError({ title: _t(t("Please select From date and To date to generate report")) });
      return;
    }

    setDataPaginating(true);
    const fromDate = moment(startDate).format("YYYY-MM-DD");
    const toDate = moment(endDate).format("YYYY-MM-DD");

    const url = BASE_URL + "/settings/get-purchased-date-supplier-report";


      let formData = new FormData();
      formData.append("fromDate", fromDate);
      formData.append("toDate", toDate);
      
      if (supplierPurchaseFilters?.branch?.id) {
        formData.append("branch_id", supplierPurchaseFilters?.branch?.id);
      }
      
      if(supplierPurchaseFilters?.suppliers?.length){
        formData.append("supplier_id", supplierPurchaseFilters?.suppliers?.map((item) => item.id));
      }

      if(supplierPurchaseFilters?.ingredientItems?.length){
        formData.append("item_ids", supplierPurchaseFilters?.ingredientItems?.map((item) => item.id));
      }

      if (supplierPurchaseFilters?.group_by?.value) {
        formData.append("group_by", supplierPurchaseFilters?.group_by?.value);
      }

      return axios
        .post(url, formData, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        })
        .then((res) => {
          setReportData(res.data || []);
          setGeneratedReport(true);
          setDataPaginating(false);

          if (option === "excel") {
            setTimeout(() => componentRef.current?.handleDownload?.(), 100);
          } else {
            setTimeout(() => thermalPrint(), 100);
          }
        })
        .catch((error) => {
          setDataPaginating(false);
          if(error.response?.data?.status === 0){
            ToastError({ title: _t(t(error.response.data.message)) });
            return
          }
          ToastError({ title: _t(t("Please try again")) });
        });
  };

  const groupByOptions = [
    { label: _t(t("Branch")), value: "Supplier" },
    { label: _t(t("Item With Supplier")), value: "Item With Supplier" },
  ];

  const header = supplierPurchaseFilters?.group_by?.value === "Item With Supplier" ? [
    { name: "Branch Name", key: "b_name" },
    { name: "Supplier Name", key: "s_name" },
    { name: "Item", key: "item" },
    { name: _t(t("Purchase Date")), render: (item) => item.purchase_date ? <Moment format="LL">{item.purchase_date}</Moment> : "-" },
    { name: _t(t("Invoice Numbers")), key: "invoice_numbers" },
    { name: _t(t("Total Quantity")), key: "total_quantity", total: 1 },
    { name: _t(t("Avg Rate")), key: "avg_rate" },
    { name: _t(t("Total Cost")), key: "total_cost", total: 1 },
    { name: _t(t("Avg Cost")), key: "average_cost" },
  ] : [
    { name: "Group Name", key: "group_name" },
    { name: _t(t("Purchase Date")), render: (item) => item.purchase_date ? <Moment format="LL">{item.purchase_date}</Moment> : "-" },
    { name: _t(t("Supplier")), key: "supplier_name" },
    { name: _t(t("Ingredient Item")), key: "ingredient_name" },
    { name: _t(t("Branch")), key: "branch_name" },
    { name: _t(t("Invoice Numbers")), key: "invoice_numbers" },
    { name: _t(t("Total Quantity")), key: "total_quantity", total: 1 },
    { name: _t(t("Avg Rate")), key: "avg_rate" },
    { name: _t(t("Total Cost")), key: "total_cost", total: 1 },
  ];

  const genrateBranchName = useCallback(() => {

    let branchName = "All Branch";

    if (supplierPurchaseFilters?.branch?.name) {
      branchName = supplierPurchaseFilters?.branch?.name;
    }

    // if (supplierPurchaseFilters?.ingredientItems?.length) {
    //   branchName += "- (" + supplierPurchaseFilters?.ingredientItems?.map((item) => item.name).join(", ") + ")";
    // }

    return branchName;
  }, [supplierPurchaseFilters?.branch?.name]);

  return (
    <>
      <Helmet>
        <title>{_t(t("Supplier Purchase Report"))}</title>
      </Helmet>

      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename="Supplier Purchase Report"
        sheet="sheet 1"
        buttonText="Supplier Purchase Report"
        ref={componentRef}
      />

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    <CustomSpinLoader loading={loading || dataPaginating} />
                    <div key="smtp-form">
                      <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="col-6 t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb">
                            <li className="fk-breadcrumb__list">
                              <span className="t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Supplier Purchase Report"))}
                              </span>
                            </li>
                          </ul>
                        </div>
                        <div className="col-6 d-flex justify-content-end">
                          <ReportTypeOptions
                            selectedOption={option}
                            onChange={handleOption}
                          />
                        </div>
                      </div>
                      <div className="row g-2 mt-2 align-items-end">
                        <div className="col-4 col-md-2 col-lg-2">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="dd/MM/yyyy"
                            dropdownMode="select"
                            className="form-control form-control-sm t-mb-15 mb-md-0"
                            placeholderText={_t(t("From date"))}
                            shouldCloseOnSelect={true}
                          />
                        </div>
                        <div className="col-4 col-md-2 col-lg-2">
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="dd/MM/yyyy"
                            dropdownMode="select"
                            className="form-control form-control-sm t-mb-15 mb-md-0"
                            placeholderText={_t(t("To date"))}
                            shouldCloseOnSelect={true}
                          />
                        </div>
                        <div className="col-6 col-md-4 col-lg-2 t-mb-15 mb-md-0">
                          <Select
                            options={branchForSearch ? branchForSearch : []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            isClearable
                            value={supplierPurchaseFilters?.branch || ""}
                            onChange={handleBranch}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select branch")) + ".."}
                          />
                        </div>
                        <div className="col-6 col-md-4 col-lg-3 t-mb-15 mb-md-0">
                          <Select
                            options={supplierForSearch ? supplierForSearch : []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            isClearable
                            isMulti
                            value={supplierPurchaseFilters?.suppliers || ""}
                            onChange={handleSupplier}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Select supplier")) + ".."}
                          />
                        </div>
                        <div className="col-6 col-md-4 col-lg-3 t-mb-15 mb-md-0">
                          <Select
                            options={ingredientItemForSearch || []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            isClearable
                            isMulti
                            value={supplierPurchaseFilters?.ingredientItems || ""}
                            onChange={handleIngredientItem}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("SelectIngredient Item")) + ".."}
                          />
                        </div>
                      </div>
                      <div className="row g-2 mt-2 justify-content-between align-items-end">
                        <div className="col-4 col-md-3 col-lg-2 t-mb-15 mb-md-0">
                          <Select
                            options={groupByOptions}
                            value={supplierPurchaseFilters?.group_by || ""}
                            onChange={handleGroupBy}
                            styles={customStyle}
                            maxMenuHeight="200px"
                            placeholder={_t(t("Group by"))}
                          />
                        </div>
                        <div className="col-2 t-mb-15 mb-md-0 text-right">
                          {/* Action Button */}
                          <button
                            className="btn btn-block btn-primary text-uppercase sm-text py-2 rounded"
                            onClick={(e) => generateReport(e)}
                            disabled={dataPaginating}
                          >
                            Generate Report
                          </button>
                        </div>
                      </div>

                      {supplierPurchaseFilters?.group_by?.value === "Item With Supplier" ? (
                        <SupplierPurchaseGroupedTable
                          header={header}
                          generatedReport={generatedReport}
                          heading={_t(t("Supplier Purchase Report"))}
                          reportData={reportData}
                          startDate={startDate}
                          endDate={endDate}
                          componentRef={componentRef}
                          printRef={componentRefThermal}
                          branch={{
                            name: genrateBranchName(),
                            show: true,
                            removeTotal: true,
                          }}
                          type={option}
                          generalSettings={generalSettings}
                        />
                      ) : (
                        <ReportExcelPrintGap
                          header={header}
                          generatedReport={generatedReport}
                          heading={_t(t("Supplier Purchase Report"))}
                          reportData={reportData}
                          startDate={startDate}
                          endDate={endDate}
                          componentRef={componentRef}
                          printRef={componentRefThermal}
                          branch={{
                            name: genrateBranchName(),
                            show: true,
                            removeTotal: true,
                          }}
                          type={option}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}
    </>
  );
};

export default SupplierPurchaseReport;
